/**
 * Me → Star Persona Gallery sub-page.
 *
 * Grid of generated frames. Tapping a tile opens the fullscreen lightbox
 * (StarPersonaLightbox) where the user can pinch-zoom, swipe between frames,
 * mark active, send, or delete.
 */

import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
// Lazy: react-zoom-pan-pinch (+ Embla) live in the lightbox — load on open only.
const StarPersonaLightbox = lazy(() =>
  import('../../components/me/StarPersonaLightbox').then((m) => ({ default: m.StarPersonaLightbox })),
);
import { AiGeneratedLabel } from '../../components/AiGeneratedLabel';
import {
  useStarPersona,
  useSelectStarPersona,
  useDeleteStarPersonaFrame,
  type StarPersonaFrame,
} from '../../hooks/useStarPersona';
import styles from './StarPersonaGallery.module.css';

export function StarPersonaGallery(): JSX.Element {
  const { t } = useTranslation(['me', 'common']);
  const { data, isLoading, isError } = useStarPersona();
  const select = useSelectStarPersona();
  const remove = useDeleteStarPersonaFrame();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const frames: StarPersonaFrame[] = data
    ? [...data.generic.frames, ...data.selfie.frames]
    : [];

  const activeR2Key = data?.selectedR2Key ?? null;
  const busy = select.isPending || remove.isPending;

  async function handleMakeActive(r2Key: string): Promise<void> {
    await select.mutateAsync(r2Key);
  }

  async function handleDelete(r2Key: string): Promise<void> {
    await remove.mutateAsync(r2Key);
  }

  return (
    <MeSubpageShell title={t('sections.starPersona')} data-testid="star-persona-gallery-page">
      {isLoading && (
        <p data-testid="gallery-loading">{t('common:states.loading')}</p>
      )}
      {isError && (
        <p role="alert" data-testid="gallery-error">{t('starPersona.galleryError')}</p>
      )}
      {!isLoading && !isError && data && frames.length === 0 && (
        <p data-testid="gallery-empty">{t('starPersona.galleryEmpty')}</p>
      )}
      {!isLoading && !isError && frames.length > 0 && (
        <>
        <AiGeneratedLabel />
        <div className={styles.grid} data-testid="gallery-grid">
          {frames.map((f, i) => {
            const isActive = activeR2Key === f.r2Key;
            return (
              <button
                key={f.r2Key}
                type="button"
                className={`${styles.tile} ${isActive ? styles.tileSelected : ''}`}
                onClick={() => setLightboxIndex(i)}
                aria-label={isActive ? t('starPersona.frameAriaActive', { n: i + 1 }) : t('starPersona.frameAria', { n: i + 1 })}
                data-testid={`gallery-tile-${f.r2Key}`}
              >
                <img src={f.url} alt={f.archetype ?? t('starPersona.frameAlt')} className={styles.img} />
              </button>
            );
          })}
        </div>
        </>
      )}

      {lightboxIndex !== null && frames.length > 0 && (
        <Suspense fallback={null}>
          <StarPersonaLightbox
            frames={frames}
            initialIndex={Math.min(lightboxIndex, frames.length - 1)}
            activeR2Key={activeR2Key}
            onClose={() => setLightboxIndex(null)}
            onMakeActive={handleMakeActive}
            onDelete={handleDelete}
            busy={busy}
          />
        </Suspense>
      )}
    </MeSubpageShell>
  );
}
